import { useCallback } from "react";

import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import axios, { type AxiosResponse } from "axios";

import { EHttpStatusCode, processRequest } from "@api/config";

import { useToysStore } from "@storage/global/useToysStore";

import { resetToysSession } from "./resetToysSession";
import { getToysSessionTokenQueryOptions } from "./toys";

interface IToysGateRequestOptions<T> {
	prefix: string;
	request: (token: string) => Promise<AxiosResponse<T>>;
}

interface ITokenRefresh {
	expiredToken: string;
	promise: Promise<string>;
}

const tokenRefreshes = new WeakMap<QueryClient, ITokenRefresh>();

const refreshToysSessionToken = (
	queryClient: QueryClient,
	expiredToken: string,
	privateShowId: string | null,
): Promise<string> => {
	const options = getToysSessionTokenQueryOptions(privateShowId);
	const cachedToken = queryClient.getQueryData(options.queryKey)?.token;
	if (cachedToken && cachedToken !== expiredToken) {
		return Promise.resolve(cachedToken);
	}

	const refresh = tokenRefreshes.get(queryClient);
	if (refresh?.expiredToken === expiredToken) {
		return refresh.promise;
	}

	// Share one renewal per expired token,
	// including failures, to avoid a refresh loop.
	const promise = queryClient.fetchQuery({ ...options, staleTime: 0 }).then(({ token }) => token);
	tokenRefreshes.set(queryClient, { expiredToken, promise });
	return promise;
};

/**
 * Requests the toys gate with a session token
 * If the token is expired, it will be refreshed and the request will be retried.
 * If the token is invalid, it will be reset and the request will be retried.
 * If status is 409, the session will be reset and the request will be retried.
 */
export const useToysGateRequest = (sessionToken: string) => {
	const queryClient = useQueryClient();
	const { activePrivateShowId } = useToysStore();

	return useCallback(
		<T>({ prefix, request }: IToysGateRequestOptions<T>) =>
			processRequest({
				prefix,
				request: async () => {
					const options = getToysSessionTokenQueryOptions(activePrivateShowId);
					const tokenQuery = queryClient.getQueryCache().find({ queryKey: options.queryKey });
					const ensureSessionIsActive = () => {
						if (
							!tokenQuery?.state.data ||
							useToysStore.getState().activePrivateShowId !== activePrivateShowId ||
							queryClient.getQueryCache().find({ queryKey: options.queryKey }) !== tokenQuery
						) {
							throw new axios.Cancel("Toys session was reset");
						}
					};

					const requestWithSession = async (token: string) => {
						ensureSessionIsActive();
						try {
							const response = await request(token);
							ensureSessionIsActive();
							return response;
						} catch (error) {
							// A late response from a closed session must not reset its replacement.
							ensureSessionIsActive();
							if (axios.isAxiosError(error) && error.response?.status === EHttpStatusCode.CONFLICT) {
								resetToysSession(queryClient);
								throw new axios.Cancel("Toys session was reset");
							}
							throw error;
						}
					};

					const token = queryClient.getQueryData(options.queryKey)?.token ?? sessionToken;
					try {
						return await requestWithSession(token);
					} catch (error) {
						if (!axios.isAxiosError(error) || error.response?.status !== EHttpStatusCode.UNAUTHORIZED) {
							throw error;
						}

						const refreshedToken = await refreshToysSessionToken(queryClient, token, activePrivateShowId);
						return requestWithSession(refreshedToken);
					}
				},
			}),
		[activePrivateShowId, queryClient, sessionToken],
	);
};
